import React, { useEffect, useState } from 'react';

import {
  VStack,
  HStack,
  Box,
  Text,
  Center,
  Skeleton,
  Switch,
  Spinner,
} from 'native-base';
import {
  UserAvatar,
  AnimatedShuffleView,
  AnonymousModeInfoBox,
  PointsTag,
  ProfileTags,
} from '@breakroom-nx/ui';
import {
  Flair,
  getAvatarURL,
  Shuffler,
  shuffle,
  User,
} from '@breakroom-nx/classes';
import { theme } from '@breakroom-nx/assets';
import { timeFrom } from '@breakroom-nx/helpers';

import styled, { keyframes } from 'styled-components';
import '/node_modules/flag-icons/css/flag-icons.min.css';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@breakroom-nx/apis';

export interface PublicProfieHeaderProps {
  readonly user: User;
  readonly flair?: Flair;
  readonly isClient: boolean;
  readonly isLoading: boolean;
}

// Styled Components
const pulseAnimation = keyframes`
  0% { width: 0.45rem; height: 0.45rem; opacity: 1}
  100% { width: 1.5rem; height: 1.5rem; opacity: 0 }
`;

const OnlineStatus = styled.div<{ $userOnline?: boolean }>`
  width: 0.45rem;
  height: 0.45rem;
  position: absolute;
  background: ${(props) =>
    props.$userOnline ? theme.colors.primary[400] : theme.colors.surface[500]};
  border-radius: 100%;
`;

const PulseBackground = styled.div`
  position: absolute;
  background: ${theme.colors.primary[400]};
  border-radius: 100%;
  animation-name: ${pulseAnimation};
  animation-duration: 2s;
  animation-iteration-count: infinite;
`;

export function PublicProfieHeader({
  user,
  isClient,
  isLoading,
  flair = undefined,
}: PublicProfieHeaderProps) {
  const [isUpdating, setUpdating] = useState<boolean>(false);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(user.isAnonymous); // Local state for anonymity
  const toggleAnonymity = httpsCallable(functions, 'user-toggleanonymity');
  const shuffleObject: Shuffler = shuffle();

  useEffect(() => {
    // console.log(`User Last Online Time: ${user.onlineStatus?.lastOnline}`);
  }, []);

  const handleAnonymousSwitch = async () => {
    setUpdating(true);

    try {
      // console.log(`Current User is Anonymous: ${isAnonymous}`);

      const avatarURL = await getAvatarURL(
        `avatars/${shuffleObject?.object.noun}.png`
      );

      const alias = `${shuffleObject?.adjective}-${shuffleObject?.object.noun}`;

      await toggleAnonymity({
        username: alias,
        theme: shuffleObject?.color,
        avatar: avatarURL,
      });

      // Add a slight delay before updating the UI
      setTimeout(() => {
        setIsAnonymous(!isAnonymous);
      }, 500); // 500ms delay for visual feedback
    } catch (err) {
      // console.error(`Unable to toggle anonymity: ${err}`);
    } finally {
      setTimeout(() => {
        setUpdating(false); // Ensure the spinner is visible for at least 500ms
      }, 500); // 500ms delay for spinner visibility
    }
  };

  if (isLoading) {
    return (
      <VStack space={8} alignItems={'center'} width={'full'}>
        <Skeleton
          width={'90px'}
          height={'90px'}
          rounded="full"
          startColor={theme.colors.surface[200]}
          endColor={theme.colors.surface[400]}
        />
        <VStack space={3} alignItems={'center'} width={'full'}>
          <Skeleton
            h="8"
            w={'1/2'}
            rounded="full"
            startColor={theme.colors.yellow[100]}
            endColor={theme.colors.yellow[200]}
          />
          <Skeleton
            h="5"
            w={'1/3'}
            rounded="full"
            startColor={theme.colors.surface[200]}
            endColor={theme.colors.surface[400]}
          />
        </VStack>
        <Skeleton.Text
          lines={3}
          alignItems="center"
          px="12"
          startColor={theme.colors.surface[200]}
          endColor={theme.colors.surface[400]}
        />
      </VStack>
    );
  }

  return (
    <VStack alignItems={'center'} space={4}>
      <Box alignItems={'center'}>
        {isAnonymous ? (
          <AnimatedShuffleView
            visible={isAnonymous}
            color={user.theme}
            avatar={user.avatar}
          />
        ) : (
          <VStack>
            <UserAvatar
              noBorder
              user={user}
              uid={user.uid}
              size={90} // 100
              // flair={flair}
              disabled={true}
            />
            <HStack width={'full'} justifyContent={'center'} mt={-4}>
              <PointsTag
                amount={user.points ?? 0}
                payment={false}
                fontSize={15}
              />
            </HStack>
          </VStack>
        )}
      </Box>

      <VStack alignItems={'center'} space={3}>
        <HStack alignItems={'baseline'} space={3}>
          <Text
            fontSize={25}
            variant={'usernameProfile'}
            color={user.theme ?? theme.colors.userDefaultColors.theme}
            isTruncated
            height={'100%'}
            py={1.5}
          >
            {user.getUsername()}
          </Text>

          {user.onlineStatus && (
            <Box>
              <Center height={'full'} width={'full'}>
                <OnlineStatus $userOnline={user.onlineStatus.isOnline} />
                {user.onlineStatus.isOnline ? <PulseBackground /> : null}
              </Center>
            </Box>
          )}
        </HStack>

        {isClient ? (
          <HStack
            alignItems={'center'}
            space={2}
            rounded={'full'}
            bg={'surface.200'}
            px={4}
            py={'8px'}
          >
            {isAnonymous ? (
              <UserAvatar
                borderColor={user.theme}
                size={30}
                uid={user.uid}
                disabled
              />
            ) : (
              <AnimatedShuffleView
                visible={true}
                color={user.theme}
                avatar={isAnonymous ? user.avatar : shuffleObject.object.avatar}
                size={30}
              />
            )}
            <Text
              fontWeight={500}
              fontSize={14}
              color={isAnonymous ? user.theme : 'white'}
            >
              {'Anonymous'}
            </Text>

            {isUpdating ? (
              <Spinner size={'sm'} />
            ) : (
              <Switch
                disabled={isUpdating}
                onToggle={handleAnonymousSwitch}
                isChecked={isAnonymous}
                onTrackColor={isAnonymous ? user.theme : 'surface.300'}
                offTrackColor={'surface.100'}
              />
            )}
          </HStack>
        ) : null}

        {user.onlineStatus && !user.onlineStatus.isOnline && (
          <Box bg={'surface.300'} rounded={'full'} px={3} py={1}>
            <Text fontSize={14} fontWeight={500} color={'surface.500'}>
              Online {timeFrom(new Date(user.onlineStatus.lastOnline), true)}{' '}
              ago
            </Text>
          </Box>
        )}
      </VStack>

      {isAnonymous ? (
        <AnonymousModeInfoBox
          color={user.theme ?? '#FFF'}
          alias={user.username}
        />
      ) : (
        <VStack space={4} alignItems={'center'}>
          <ProfileTags user={user} />

          {user.bio && (
            <Box
              py={3}
              px={5}
              borderColor={theme.colors.surface[300]}
              borderWidth={1}
              borderRadius={'sm'}
            >
              <Text color={'white'} fontWeight={'regular'} textAlign={'center'}>
                {user?.bio}
              </Text>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
}

export default React.memo(PublicProfieHeader);
